import { isEmpty } from '@/pages/intelligence/utils/commonUtils';
import { SegmentOption } from '@/pages/intelligence/pages/PCConsoleBenchmark/types';
import type { IEntity } from '../types';

interface IGameTypeItem {
  type?: string;
  id?: string;
}

interface INormalizableMobileGame {
  typeIdList?: IGameTypeItem[];
  typeList?: string[];
  __mobileGameTypesNormalized?: boolean;
}

/**
 * 获取实体主键列表
 * @param entities 实体
 * @param entityKey 实体主键字段名
 * @returns 实体主键列表
 */
export function getEntitiesKeys(entities: IEntity[], entityKey: string): string[] {
  if (!Array.isArray(entities)) return [];
  const keys: string[] = [];
  for (const entity of entities) {
    const candidates = [entityKey, 'app_id', 'appId', 'id', 'unified_id', 'game_id', 'gameId'];
    let resolvedKey: string | undefined;
    for (const keyField of candidates) {
      const value = keyField && entity?.[keyField];
      if (!isEmpty(value)) {
        resolvedKey = value;
        break;
      }
    }
    if (isEmpty(resolvedKey)) continue;
    keys.push(resolvedKey as string);
  }
  return keys;
}

export function normalizeMobileSearchGameTypes(types: string[] = []): string[] {
  if (!Array.isArray(types) || !types.length) {
    return [];
  }

  const uniqueTypes = Array.from(new Set(types.filter(Boolean)));
  const hasMobile = uniqueTypes.includes('mobile');
  const hasCombine = uniqueTypes.includes('combine');
  // const hasPc = uniqueTypes.includes('pc');
  // const hasConsole = uniqueTypes.includes('console');

  if (hasCombine && hasMobile && uniqueTypes.length === 2) {
    return ['mobile'];
  }

  // 合并pc、console，但是看 studio hub 那边又同时展示了pc、console、combine 3个图标，都不知道要不要合并了，代码暂时先留着
  // if (hasPc && hasConsole) {
  //   const normalizedTypes: string[] = [];
  //   let hasMergedPcConsole = false;
  //
  //   uniqueTypes.forEach((type) => {
  //     if (['pc', 'console', 'combine'].includes(type)) {
  //       if (!hasMergedPcConsole) {
  //         normalizedTypes.push('combine');
  //         hasMergedPcConsole = true;
  //       }
  //       return;
  //     }
  //
  //     normalizedTypes.push(type);
  //   });
  //
  //   return normalizedTypes;
  // }

  return uniqueTypes;
}

export function normalizeMobileSearchGame<T extends IEntity & INormalizableMobileGame>(
  game: T,
): T {
  if (isEmpty(game)) {
    return game;
  }

  if (game?.__mobileGameTypesNormalized) {
    return game;
  }

  const typeIdList = Array.isArray(game?.typeIdList) ? game.typeIdList : [];
  const rawTypeList = Array.isArray(game?.typeList) ? game.typeList : [];
  const currentTypes = typeIdList.length
    ? typeIdList.map((item) => item?.type).filter(Boolean) as string[]
    : rawTypeList.filter(Boolean);
  const normalizedTypeList = normalizeMobileSearchGameTypes(currentTypes);

  if (!normalizedTypeList.length) {
    return game;
  }

  let normalizedTypeIdList = typeIdList;
  if (typeIdList.length) {
    const typeItemMap = new Map<string, IGameTypeItem>();
    typeIdList.forEach((item) => {
      if (!item?.type || typeItemMap.has(item.type)) {
        return;
      }
      typeItemMap.set(item.type, item);
    });

    normalizedTypeIdList = normalizedTypeList
      .map((type) => {
        if (type === 'combine') {
          const combineItem = typeItemMap.get('combine')
            || typeItemMap.get('pc')
            || typeItemMap.get('console');
          if (!combineItem) {
            return null;
          }

          return {
            ...combineItem,
            type: 'combine',
          };
        }

        return typeItemMap.get(type) || null;
      })
      .filter(Boolean) as IGameTypeItem[];
  }

  return {
    ...game,
    typeIdList: normalizedTypeIdList,
    typeList: normalizedTypeList,
    __mobileGameTypesNormalized: true,
  };
}

export function normalizeMobileSearchGames<T extends IEntity & INormalizableMobileGame>(
  games: T[],
): T[] {
  if (!Array.isArray(games) || !games.length) {
    return [];
  }

  return games.map((game) => normalizeMobileSearchGame(game));
}

// pcconsole-compare快捷键
export const SHORTCUTPCCONSOLELIST: SegmentOption[] = [
  {
    text: 'POE-like',
    value: 'POE-like',
    name: 'POE-like',
  },
  {
    text: 'Tarkov-like',
    value: 'Tarkov-like',
    name: 'Tarkov-like',
  },
  {
    text: 'Dying Light 2-related',
    value: 'Dying Light 2-Related',
    name: 'Dying Light 2-related',
  },
  {
    text: 'Dune-related',
    value: 'Dune-related',
    name: 'Dune-related',
  },
];
